import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC } from 'react';
import { object, string } from 'yup';
import { Close, Save } from '@mui/icons-material';
import { Box, Button, Fade, Grid, TextField, Typography, Alert } from '@mui/material';
import { Loader, Modal } from '../../../../components';
import { addPartAbbreviation, updatePartAbbreviation } from '../../../../fetch';
import { IPartAbbreviation } from '../../../../models';

interface IPartAbbreviationModal {
  open: boolean;
  onClose: (shouldRefetch?: boolean) => void;
  currentPartAbbreviation?: IPartAbbreviation;
}

const PartAbbreviationSchema = object().shape({
  abbreviation: string().max(255, 'Max 255 characters').required('Required')
});

export const PartAbbreviationModal: FC<IPartAbbreviationModal> = ({
  open,
  onClose,
  currentPartAbbreviation
}) => {
  const { enqueueSnackbar } = useSnackbar();

  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          abbreviation:
            currentPartAbbreviation && currentPartAbbreviation.abbreviation
              ? currentPartAbbreviation.abbreviation
              : ''
        }}
        validationSchema={PartAbbreviationSchema}
        onSubmit={async (values, actions) => {
          try {
            if (currentPartAbbreviation && currentPartAbbreviation.partAbbreviationId) {
              await updatePartAbbreviation(currentPartAbbreviation.partAbbreviationId, values);
            } else {
              await addPartAbbreviation(values);
            }

            enqueueSnackbar(currentPartAbbreviation ? 'Option Updated!' : 'Option Created!', {
              variant: 'success'
            });
            onClose(true);
            actions.resetForm();
          } catch (error) {
            enqueueSnackbar(error?.Detail ?? 'Error saving option.', {
              variant: 'error'
            });
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          errors,
          touched,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  onClose();
                }
              }}
              maxWidth="xs"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box marginBottom="1rem">
                    <Typography variant="h5">
                      {currentPartAbbreviation ? `Edit Option` : 'Add Option'}
                    </Typography>
                  </Box>
                  <Alert severity="info" sx={{ marginBottom: '1rem' }}>
                    Enter the word or phrase that should be All Capitals. These should be standalone
                    phrases like "GE" and "LVP"
                  </Alert>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Option"
                        name="abbreviation"
                        value={values.abbreviation}
                        onBlur={handleBlur}
                        size="small"
                        onChange={e => setFieldValue('abbreviation', e.target.value)}
                      />
                    </Grid>
                  </Grid>
                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
